export interface SubAssignmentItem {
    workorder_id: number
    user_id: number
    sub_price: number
}

export interface BatchAssignSubPayload {
    assignments: SubAssignmentItem[]
}

/** API decimal amounts serialized as strings */
export type DecimalString = string

/** Raw workorder line from GET summary response */
export interface SubContractSummaryWorkorderLineRaw {
    id: number
    status: string
    payout_amount: DecimalString
    service_area_count: number
    service: { id: number; name: string } | null
    unit: { id: number; name: string } | null
    apartment: { id: number; name: string } | null
    start_date: string | null
}

/** Raw sub group from GET summary response */
export interface SubContractSummarySubGroupRaw {
    user_id: number
    name: string | null
    workorder_count: number
    total_payout: DecimalString
    workorders: SubContractSummaryWorkorderLineRaw[]
}

/** Raw top-level response */
export interface SubContractSummaryResponseRaw {
    grand_total_payout: DecimalString
    workorder_count: number
    subs: SubContractSummarySubGroupRaw[]
}

export interface SubContractSummaryWorkorderLineView {
    id: number
    status: string
    payout: number
    service_area_count: number
    service_name: string
    unit_name: string
    property_id: number | null
    property_name: string
    /** ISO-8601 or null when workorder has no start_date */
    start_date: string | null
}

export interface SubContractSummarySubView {
    user_id: number
    display_name: string
    workorder_count: number
    payout_total: number
    workorders: SubContractSummaryWorkorderLineView[]
}

export interface SubContractSummaryViewModel {
    grand_total_payout: number
    workorder_count: number
    subs: SubContractSummarySubView[]
}

export type GetSubContractSummaryParams = {
    /** Omit both for all-time (no date query params). Use MM/DD/YYYY when set. */
    start_date?: string
    end_date?: string
    /** Filter to one jobsite/property (apartment id). */
    apartment?: number
    /** Work order status filter; omit or `ALL` for no status filter. */
    wo_status?: string
}

const parseDecimal = (s: DecimalString | undefined | null): number => {
    if (s == null || s === '') {
        return 0
    }
    const n = Number(s)
    return Number.isFinite(n) ? n : 0
}

export const mapSubContractSummaryToViewModel = (
    raw: SubContractSummaryResponseRaw | null | undefined,
): SubContractSummaryViewModel => {
    if (!raw || typeof raw !== 'object') {
        return {
            grand_total_payout: 0,
            workorder_count: 0,
            subs: [],
        }
    }

    const subs: SubContractSummarySubView[] = (raw.subs ?? []).map((g) => ({
        user_id: g.user_id,
        display_name: g.name?.trim() || `User ${g.user_id}`,
        workorder_count: g.workorder_count ?? 0,
        payout_total: parseDecimal(g.total_payout),
        workorders: (g.workorders ?? []).map((w) => ({
            id: w.id,
            status: w.status,
            payout: parseDecimal(w.payout_amount),
            service_area_count: w.service_area_count ?? 0,
            service_name: w.service?.name ?? '',
            unit_name: w.unit?.name ?? '',
            property_id: w.apartment?.id ?? null,
            property_name: w.apartment?.name ?? '',
            start_date: w.start_date,
        })),
    }))

    return {
        grand_total_payout: parseDecimal(raw.grand_total_payout),
        workorder_count: raw.workorder_count ?? 0,
        subs,
    }
}
